import { FC, useEffect, useState, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Edit, Add } from '@mui/icons-material';
import { Button, Theme, Button as SMButton, Box } from '@mui/material';
import { Table, Pagination, Page, PageTitleWithButton, Loader } from '../../../components';
import { UserManagementFilters } from './UserManagementFilters';
import { AddEditVendor } from './AddEditVendorModal';
import { useSnackbar } from 'notistack';
import {
  getVendorManagementUsers,
  getVendorUserManagementVendorUser,
  getVendorUserManagementVendors,
  getVendorUserManagementVendorsUser
} from '../../../fetch';
import { IDropdownResponse, IVendorUser, IVendorDetails } from '../../../models';

interface IUserManagement {}

export const UserManagement: FC<IUserManagement> = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isModalOpen, showModal] = useState<boolean>(false);
  const [currentVendor, setCurrentVendor] = useState<IVendorDetails>(null);
  const [loadingVendor, setLoadingVendor] = useState(false);

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingVendorManagementUsers, setLoadingVendorManagementUsers] = useState<boolean>(true);
  const [vendorManagementUsers, setVendorManagementUsers] = useState<IVendorUser[]>([]);
  const [isLoadingUserManagementVendors, setLoadingUserManagementVendors] = useState<boolean>(true);
  const [userManagementVendors, setUserManagementVendors] = useState<IDropdownResponse[] | null>(
    null
  );
  const [selectedUserManagementVendor, setSelectedUserManagementVendor] = useState<any>();
  const [initialUserManagementVendor, setInitialUserManagementVendor] = useState<any>();
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    Name?: 'Asc' | 'Desc';
    Email?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });

  const [filters, setFilters] = useState<{
    userManagementVendor?: number | string;
  } | null>({});

  const fetchVendorManagementUsers = async () => {
    try {
      setLoadingVendorManagementUsers(true);
      const res = await getVendorManagementUsers(
        selectedUserManagementVendor || initialUserManagementVendor,
        {
          page: page + 1,
          perPage: perPage,
          sortBy: selectedSort,
          sortDirection: sortDirection[selectedSort] as string
        }
      );
      setRecordCount(res.totalRecordCount);
      setVendorManagementUsers(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading users, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingVendorManagementUsers(false);
    }
  };

  const fetchUserManagementVendors = async () => {
    try {
      setLoadingUserManagementVendors(true);
      const res = await getVendorUserManagementVendors();
      setUserManagementVendors(res);
      setInitialUserManagementVendor(res[0].value);
      setSelectedUserManagementVendor(res[0].value);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading Vendors, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingUserManagementVendors(false);
    }
  };

  const [editOptions, setEditOptions] = useState(null);
  const handleEditVendor = async id => {
    setLoadingVendor(true);
    try {
      const vendor = await getVendorUserManagementVendorUser(selectedUserManagementVendor, id);
      const vendorOptions = await getVendorUserManagementVendorsUser(id);
      setEditOptions(vendorOptions);
      setCurrentVendor(vendor);
      showModal(true);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingVendor(false);
    }
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  useEffect(() => {
    fetchUserManagementVendors();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialUserManagementVendor || selectedUserManagementVendor) {
      fetchVendorManagementUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUserManagementVendor, sortDirection, selectedSort, page, perPage]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Asc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Email',
        accessor: 'email',
        isServerSorted: selectedSort === 'Email',
        isServerSortedDesc: sortDirection.Email === 'Desc',
        handleClickColumn: () => handleClickColumn('Email')
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 300,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorUser } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                className={classes.editButton}
                onClick={() => {
                  handleEditVendor(original.personId);
                }}
              >
                Edit
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorManagementUsers]);

  return (
    <>
      {loadingVendor && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="User Management">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'User Management'
          }}
          button={
            <Box display="flex">
              <SMButton
                id="create-job"
                startIcon={<Add />}
                color="primary"
                variant="contained"
                className={classes.addUserButton}
                onClick={() => showModal(true)}
              >
                Add User
              </SMButton>
            </Box>
          }
        />
        <UserManagementFilters
          isLoading={isLoadingVendorManagementUsers || isLoadingUserManagementVendors}
          applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
            setPage(0);
            setFilters({
              ...filters,
              userManagementVendor: selectedUserManagementVendor || undefined
            });
            fetchVendorManagementUsers();
          }}
          userManagementVendorOptions={userManagementVendors}
          setSelectedUserManagementVendor={setSelectedUserManagementVendor}
          selectedUserManagementVendor={selectedUserManagementVendor}
        />
        <Table
          columns={columns}
          data={vendorManagementUsers}
          isLoading={isLoadingVendorManagementUsers || isLoadingUserManagementVendors}
          hidePagination
        />
        {!isLoadingVendorManagementUsers && !isLoadingUserManagementVendors && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Page>
      <AddEditVendor
        open={isModalOpen}
        onClose={() => {
          showModal(false);
          setCurrentVendor(null);
          setEditOptions(null);
        }}
        currentVendor={currentVendor}
        fetchVendorManagementUsers={fetchVendorManagementUsers}
        userManagementVendorOptions={userManagementVendors}
        selectedUserManagementVendor={selectedUserManagementVendor}
        editOptions={editOptions}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButton: {
    textAlign: 'right',
    fontSize: 13,
    '&& + &&': {
      marginLeft: theme.spacing(0.5)
    },
    '&& .MuiButton-startIcon': {
      position: 'relative',
      top: '-2px'
    }
  },
  actionButtons: {
    textAlign: 'right'
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  activateButton: {
    marginLeft: theme.spacing(0.5)
  },
  clearSearchButton: {
    '@media (min-width: 408px)': {
      marginTop: 3,
      marginLeft: theme.spacing(1)
    }
  },
  addUserButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  }
}));
